import {
  AloftDate,
  AloftPrice,
  ToolkitListingID,
  orUndefined,
  parseToolkitListingID,
} from '@aloft/toolkit-shared';

import { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';

import { useTkv2UpdateReferenceBarProperties } from '../../../controller/hooks/useTkv2UpdateReferenceBarProperties.ts';
import { formatReferenceViewID } from '../../../modules/referenceView/index.ts';
import { featuresSelectors } from '../../../redux/features/selectors/featuresSelectors.ts';
import { selectEffectiveDate } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectPriorSales } from '../../../redux/features/selectors/priorSalesSelectors.ts';
import { selectPropertyIdentificationById } from '../../../redux/features/selectors/propertyDataSelectors/propertyIdentificationSelectors.ts';
import { selectRecommendedCompsStatusGroups } from '../../../redux/features/selectors/uiSelectors.ts';
import { toggleRecommendedCompsStatusGroup } from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import {
  LocationType,
  getLocationTypeProximityRequirementMiles,
} from '../../../utils/locationTypeProximityRequirementMiles.ts';
import { getLocationTypeSaleDateRequirementMonths } from '../../../utils/locationTypeSaleDateRequirementMonths.ts';
import { ToolkitPropertyTableUI } from '../../ToolkitPropertyTable/ToolkitPropertyTableUI.tsx';
import { useRecommendedCompSelectionTable } from '../hooks/useRecommendedCompSelectionTable.tsx';
import { CompSelectionProperty, RecommendedCompSelectionGridProps } from '../types.ts';
import {
  RECOMMENDED_COMPS_STATUS_GROUP_ORDER,
  RecommendedCompsStatusGroup,
  getStatusGroup,
} from '../utils/getStatusGroup.ts';
import { StatusGroupHeaderRow } from './gridHelpers/StatusGroupHeaderRow.tsx';

export const CompSelectionGrid = forwardRef(
  (
    {
      data,
      subject,
      comps,
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      focusedPropertyIndex,
      setFocusedRow,
      source,
      orderID,
      isInspectifyOrder,
      hasArvOption,
      compSelectionTableVariant = 'sales',
    }: RecommendedCompSelectionGridProps,
    ref,
  ) => {
    const dispatch = useAppDispatch();
    const statusGroups = useAppSelector(selectRecommendedCompsStatusGroups);
    const subjectPropertyIdentification = useAppSelector((state) =>
      selectPropertyIdentificationById(state, 'subject'),
    );
    const locationType = subjectPropertyIdentification?.locationType as LocationType | undefined;
    const proximityRequirementMiles = getLocationTypeProximityRequirementMiles(locationType);
    const saleDateRequirementMonths = getLocationTypeSaleDateRequirementMonths(locationType);
    const effectiveDate = useAppSelector(selectEffectiveDate);
    const effectiveDateAloft = effectiveDate ? AloftDate.schema.parse(effectiveDate) : undefined;
    const priorSales = useAppSelector(selectPriorSales);
    const { subjectPriorSaleDate, subjectPriorSalePrice } = useMemo(() => {
      const subjectPriorSale = priorSales.subject?.[0];
      return {
        subjectPriorSaleDate:
          orUndefined(AloftDate.fromIsoString)(subjectPriorSale?.CloseDate) ?? undefined,
        subjectPriorSalePrice:
          orUndefined(AloftPrice.fromDollarLike)(subjectPriorSale?.ClosePrice) ?? undefined,
      };
    }, [priorSales.subject]);
    const { selectShouldAllowRecommendedCompsImageAnalysisUADRatings } = featuresSelectors;
    const imageAnalysisRatingsEnabled = useAppSelector(
      selectShouldAllowRecommendedCompsImageAnalysisUADRatings,
    );
    const { table, rowsToPin } = useRecommendedCompSelectionTable(
      data,
      subject,
      comps,
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      source,
      orderID,
      isInspectifyOrder,
      hasArvOption,
      locationType,
      proximityRequirementMiles,
      effectiveDateAloft,
      saleDateRequirementMonths,
      compSelectionTableVariant,
      imageAnalysisRatingsEnabled,
      subjectPriorSaleDate,
      subjectPriorSalePrice,
    );
    const updateRefBarProperties = useTkv2UpdateReferenceBarProperties();

    useImperativeHandle(ref, () => table, [table]);

    const handleRowClick = useCallback(
      (row: CompSelectionProperty) => {
        if (row.id === 'subject') {
          updateRefBarProperties({
            id: formatReferenceViewID({ type: 'subject' }),
            mapPinLabel: '',
          });
        } else {
          const parsedListingID = parseToolkitListingID(row.listingID as ToolkitListingID);
          if (parsedListingID) {
            updateRefBarProperties({
              id: formatReferenceViewID(parsedListingID),
              mapPinLabel: row.similarityRank?.toString() ?? '',
            });
          }
        }
        setFocusedRow(row.id);
      },
      [setFocusedRow, updateRefBarProperties],
    );

    const getRowBackgroundColor = useCallback(
      (row: { original: CompSelectionProperty }) => {
        if (!isInspectifyOrder || !hasArvOption || row.original.id === 'subject') {
          return;
        }

        const selection = rowSelection[row.original.id];
        if (selection?.aiv) {
          return '#FFF6C7';
        }
        if (selection?.arv) {
          return '#E0F4F0';
        }
      },
      [hasArvOption, isInspectifyOrder, rowSelection],
    );

    const groupCounts = useMemo(() => {
      const counts: Record<RecommendedCompsStatusGroup, number> = {
        sold: 0,
        active: 0,
        pending: 0,
      };
      data.forEach((property) => {
        const group = getStatusGroup(property.mlsStatus);
        if (group) counts[group] += 1;
      });
      return counts;
    }, [data]);

    const handleToggleGroup = useCallback(
      (groupId: RecommendedCompsStatusGroup) => {
        dispatch(toggleRecommendedCompsStatusGroup(groupId));
      },
      [dispatch],
    );

    const rowGrouping = useMemo(() => {
      if (compSelectionTableVariant !== 'sales') return;

      return {
        getGroupId: (row: CompSelectionProperty) => getStatusGroup(row.mlsStatus),
        groups: RECOMMENDED_COMPS_STATUS_GROUP_ORDER.map((id) => ({
          id,
          count: groupCounts[id],
          collapsed: statusGroups[id].collapsed,
          renderHeader: (colSpan: number) => (
            <StatusGroupHeaderRow
              groupId={id}
              count={groupCounts[id]}
              collapsed={statusGroups[id].collapsed}
              colSpan={colSpan}
              onToggle={() => handleToggleGroup(id)}
            />
          ),
        })),
      };
    }, [compSelectionTableVariant, groupCounts, handleToggleGroup, statusGroups]);

    return (
      <ToolkitPropertyTableUI
        table={table}
        shouldPinFirstRow={rowsToPin}
        onRowClick={handleRowClick}
        activeRowIndex={focusedPropertyIndex}
        getRowBackgroundColor={getRowBackgroundColor}
        rowGrouping={rowGrouping}
        memoizeRows
      />
    );
  },
);
